import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from "@ngx-translate/core";
import { passwordRegexValidator } from '@src/app/shared/validators/password.validator';
import { confirmPasswordValidator } from '@src/app/shared/validators/confirmPassword.validator';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { SessionService } from '@src/app/shared/services/session.service';
import { UserResponse, UsersService, UserRequest, SetPasswordRequest, DisableMfaRequest } from '@src/app/shared/backend/clients';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { latinCharsetRegexValidator } from '@src/app/shared/validators/latinCharacters.validator';
import { emailRegexValidator } from '@src/app/shared/validators/email.validator';
import { MfaService, VerifyTokenRequest } from '@src/app/shared/backend/mfa';

@Component({
    selector: 'app-your-account',
    templateUrl: './your-account.component.html',
    styleUrls: ['./your-account.component.scss'],
    host: {
        class: 'app-your-account'
    },
    standalone: false
})
export class YourAccountComponent implements OnInit, OnDestroy {

  allowUserChangePassword: boolean = false;

  allowUserUpdate: boolean = false;

  displayChangePasswordModalbox: boolean = false;
  passwordUpdate: boolean = false;

  canSwitchCompanies: boolean = false;
  customersListDropdown: any = [];
  activeCompany: any = 0;

  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  displayMfaModalbox: boolean = false;
  qrData: string = '';
  mfaConfigKey: string = '';
  verificationError: boolean = false;
  isMfaMandatory: boolean = false;
  userHasMfaEnabled: boolean = false;
  displayMfaUnenrollModalbox: boolean = false;

  user: UserResponse = {};
  userData: any | undefined;

  displayChangePasswordBlock: boolean = false;

  private unsubscribePassChange$ = new Subject<void>();

  public yourAccountForm: FormGroup = this.fb.group({
    username: [{value: '', disabled: true}, [Validators.required, Validators.maxLength(100)]],
    firstname: ['', [Validators.required, Validators.maxLength(50)]],
    lastname: ['', [Validators.required, Validators.maxLength(50)]],
    romanisedFirstName: ['', [latinCharsetRegexValidator()]],
    romanisedLastName: ['', [latinCharsetRegexValidator()]],
    role: [{ value: null, disabled: true }],
    email: ['', [Validators.required, emailRegexValidator(), Validators.maxLength(50)]]
  });

  public updatePasswordForm: FormGroup = this.fb.group({
    password: ['', [Validators.required, passwordRegexValidator()]],
    confirmPassword: ['', {
      validators: [Validators.required, confirmPasswordValidator('password')],
      updateOn: 'change'
    }]
  });

  public mfaVerificationCodeForm: FormGroup = this.fb.group({
    verificationCode: ['', [Validators.required]]
  });

  constructor(
    private translate: TranslateService,
    private session: SessionService,
    private permissionService: PermissionService,
    private usersService: UsersService,
    private toasterService: ToasterService,
    private fb: FormBuilder,
    public router: Router,
    private mfaService: MfaService) {
  }

  async ngOnInit() {

    this.session.currentCompanyData$.subscribe(company => {
      this.allowUserChangePassword = company?.allowUserChangePassword ?? false;
    });

    this.getUserData();
    this.passwordUpdate = await this.permissionService.hasActionPermissions(Actions.changepassword);
    this.allowUserUpdate = await this.permissionService.hasActionPermissions(Actions.modify_users);
  }

  private async getUserData() {
    try {
        this.userData = await firstValueFrom(this.usersService.apiUsersProfileGet());
        this.userHasMfaEnabled = this.userData.isMfaEnabled;
        this.isMfaMandatory = this.userData.company?.isMfaMandatory ?? false;

        this.initForm();
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  }

  private initForm() {
    this.yourAccountForm.patchValue({
      username: this.userData?.username || null,
      firstname: this.userData?.firstName || null,
      lastname: this.userData?.lastName || null,
      romanisedFirstName: this.userData?.romanisedFirstName || null,
      romanisedLastName: this.userData?.romanisedLastName || null,
      role: this.userData.role?.name || null,
      email: this.userData?.email || null
    });

    // Update confirmPassword validity when password changes
    this.updatePasswordForm.get('password')?.valueChanges.pipe(
      takeUntil(this.unsubscribePassChange$)
    ).subscribe(() => {
      this.updatePasswordForm.get('confirmPassword')?.updateValueAndValidity();
    });
  }

  async submitForm() {
    if (this.yourAccountForm.valid) {
      const payload: UserRequest = {
        username: this.yourAccountForm.controls['username'].value,
        firstName: this.yourAccountForm.controls['firstname'].value,
        lastName: this.yourAccountForm.controls['lastname'].value,
        romanisedFirstName: this.yourAccountForm.controls['romanisedFirstName'].value,
        romanisedLastName: this.yourAccountForm.controls['romanisedLastName'].value,
        email: this.yourAccountForm.controls['email'].value,
        roleId: this.userData.role?.id
      };

      await firstValueFrom(this.usersService.apiUsersUpdateIdPut(this.userData?.id, payload));
    }
  }

  onOpenChangePasswordModalbox() {
    this.showPassword = false;
    this.showConfirmPassword = false;
    this.displayChangePasswordModalbox = true;
  }

  onCancelChangePassword() {
    this.showPassword = false;
    this.showConfirmPassword = false;
    this.displayChangePasswordModalbox = false;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  async onSubmitChangePasswordForm() {
    if (this.updatePasswordForm.valid) {
        const payload: SetPasswordRequest = {
          password: this.updatePasswordForm.controls['password'].value
        }

        const updatePassword = await firstValueFrom(this.usersService.apiUsersChangePasswordPut(payload));

        if (!updatePassword) {
          this.displayChangePasswordModalbox = false;

          const toastTile = this.translate.instant('YOUR_ACCOUNT.TOAST_PASSWORD_CHANGE_TITLE_OK');
          const toastMessage = this.translate.instant('YOUR_ACCOUNT.TOAST_PASSWORD_CHANGE_DESCRIPTION_OK');
          this.toasterService.show('success', toastTile, toastMessage);
        } else {
          const toastTile = this.translate.instant('YOUR_ACCOUNT.TOAST_PASSWORD_CHANGE_TITLE_KO');
          const toastMessage = this.translate.instant('YOUR_ACCOUNT.TOAST_PASSWORD_CHANGE_DESCRIPTION_KO');
          this.toasterService.show('error', toastTile, toastMessage);
        }

      this.displayChangePasswordModalbox = false;
    }
  }

  async onOpenMfaModalbox() {
    this.displayMfaModalbox = true;
    this.verificationError = false;

    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        throw new Error('No access token found.');
      }

      // Tmp stored for verify step
      sessionStorage.setItem('mfaToken', accessToken);

      const associatePayload = { token: accessToken };
      const response = await firstValueFrom(this.mfaService.apiMfaAssociatePost(associatePayload));

      if (response?.url) {
        this.qrData = response.url;
        sessionStorage.setItem('mfaQrUrl', this.qrData);

        if (response?.mfaConfigurationKey) {
          this.mfaConfigKey = response.mfaConfigurationKey;
          sessionStorage.setItem('mfaConfigKey', this.mfaConfigKey);
        }

      } else {
        console.error('MFA QR URL missing in response.');
        this.verificationError = true;
      }
    } catch (error) {
      console.error('Failed to open MFA modal:', error);
      this.verificationError = true;
    }
  }

  onCancelMfa() {
    this.displayMfaModalbox = false;
  }

  onConfirmUnenrollMfa() {
    this.displayMfaUnenrollModalbox = true;
  }

  async onUnenrollMfa() {
    try {

      if (!this.userData?.username) {
        this.toasterService.show('error', 'Invalid user data', 'Username is required.');
        return;
      }

      this.displayMfaUnenrollModalbox = false;

      const payload: DisableMfaRequest = {
        username: this.userData?.username
      };

      await firstValueFrom(this.usersService.apiUsersMfaDisablePost(payload));
      this.userHasMfaEnabled = false;

      const toastTitle = this.translate.instant('OTP.UNENROLL_SUCCESS_TITLE');
      const toastMessage = this.translate.instant('OTP.UNENROLL_SUCCESS_MESSAGE');
      this.toasterService.show('success', toastTitle, toastMessage);

    } catch (err) {
      console.error('Unenroll MFA failed:', err);
      this.toasterService.show('error',
        this.translate.instant('OTP.UNENROLL_FAILED_TITLE'),
        this.translate.instant('OTP.UNENROLL_FAILED_MESSAGE')
      );
    }
  }

  copyMfaKey() {
    if (!this.mfaConfigKey) return;

    navigator.clipboard.writeText(this.mfaConfigKey).then(() => {
      const toastTitle = this.translate.instant('OTP.COPY_SUCCESS_TITLE');
      const toastMessage = this.translate.instant('OTP.COPY_SUCCESS_MESSAGE');
      this.toasterService.show('success', toastTitle, toastMessage);
    }).catch(err => {
      console.error('Failed to copy MFA key:', err);
    });
  }

  async verify() {
    this.verificationError = false;

    const token = sessionStorage.getItem('mfaToken');
    const userCode = this.mfaVerificationCodeForm.controls['verificationCode'].value;

    if (!userCode || !token) {
      console.error('Missing verificationCode or token');
      this.verificationError = true;
      return;
    }

    const payload: VerifyTokenRequest = {
      token,
      userCode
    };

    try {
      const mfaVerify = await firstValueFrom(this.mfaService.apiMfaVerifyPost(payload));

      // MFA completed
      if (mfaVerify?.response === 'SUCCESS') {

        // remove Tmp data
        sessionStorage.removeItem('mfaQrUrl');
        sessionStorage.removeItem('mfaConfigKey');
        sessionStorage.removeItem('mfaToken');

        const toastTitle = this.translate.instant('OTP.TOAST_MFA_SUCCESS_TITLE');
        const toastMessage = this.translate.instant('OTP.TOAST_MFA_SUCCESS_MESSAGE');
        this.toasterService.show('success', toastTitle, toastMessage);

        this.userHasMfaEnabled = true;
        this.displayMfaModalbox = false;
      } else {
        console.warn('Unexpected response from MFA verify:', mfaVerify);
        this.verificationError = true;
      }

    } catch (err) {
      console.error('MFA verify error:', err);
      this.verificationError = true;
    }
  }

  ngOnDestroy() {
    this.unsubscribePassChange$.next();
    this.unsubscribePassChange$.complete();
  }
}
